import {useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {useQuery, useQueryClient} from 'react-query';
import {useNavigate} from 'react-router-dom';
import {Button} from '../../../../../../ui/atoms/button';
import {Avatar} from '../../../../../../ui/atoms/avatar';
import {
  ArrowHeadLeftIcon,
  ArrowHeadRightIcon,
  ArrowRightIcon,
} from '../../../../../../ui/atoms/icons';
import {LockIcon} from '../../../../../../ui/atoms/icons/lock';
import {HorizontalDots} from '../../../../../../ui/atoms/icons/horizontal-dots';
import {ItemLoaderLine} from '../../../../../../ui/organisms/item-loader-line';
import {useStoreContext} from '../../../../../../store/store-context';
import {groupController} from '../admin-group/admin-group-controller';
import {groupRequestImpl} from '../admin-group/admin-group-request';
import {
  MemberAction,
  MemberActionButton,
  MemberMenu,
  MemberMenuButton,
} from '../admin-member/member-redesign.styles';
import {
  CardActions,
  CardDivider,
  CardFooter,
  CardHeader,
  EmptyState,
  FilterButton,
  Filters,
  GroupCard,
  GroupGrid,
  GroupLead,
  GroupMeta,
  GroupTitle,
  LeadDetails,
  LeadLabel,
  LeadName,
  Loader,
  MemberAvatar,
  MemberAvatars,
  MemberCount,
  MembersSummary,
  Page,
  PageHeader,
  PageHeading,
  PageSubtitle,
  PageTitle,
  Pagination,
  PaginationButton,
  PaginationStatus,
  TypePill,
  ViewGroupButton,
} from './teams-and-groups-style';
import {GroupEditSidecar} from './group-edit-sidecar';
import {CreateGroupDialog} from '../admin-group/create-group-dialog';

const GROUPS_PER_PAGE = 9;

type GroupTypeValue = string | {id?: string; name?: string};

export interface TeamsAndGroupsGroup {
  id: string;
  name: string;
  isDefault?: boolean | string;
  groupType?: GroupTypeValue;
  groupAdmin?:
    | string
    | {
        id?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        avatar?: {url?: string};
      };
  members?: any[];
  membersCount?: number;
}

const normalizeGroups = (response: any): TeamsAndGroupsGroup[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const normalizeGroupMembers = (
  response: any,
  users: any[] = [],
): any[] => {
  let members: any[] = [];

  if (Array.isArray(response?.members)) members = response.members;

  if (members.length === 0 && Array.isArray(response)) {
    const groupWithMembers = response.find((item) =>
      Array.isArray(item?.members),
    );

    members = groupWithMembers ? groupWithMembers.members : response;
  }

  if (members.length === 0 && Array.isArray(response?.results)) {
    members = response.results;
  }

  if (members.length === 0 && Array.isArray(response?.data)) {
    members = response.data;
  }

  const restrictToWorkspaceUsers = users.length > 0;

  return members
    .map((member) => {
      const memberId = typeof member === 'string' ? member : member?.id;
      const linkedUser = member?.user;
      const linkedUserId =
        typeof linkedUser === 'object' ? linkedUser?.id : linkedUser;
      const workspaceUser = users.find(
        (user) => user.id === (linkedUserId || memberId),
      );

      if (restrictToWorkspaceUsers) return workspaceUser;

      return (
        (typeof linkedUser === 'object' && linkedUser) ||
        member
      );
    })
    .filter(Boolean);
};

export const paginateGroups = <T,>(
  groups: T[],
  page: number,
  pageSize = GROUPS_PER_PAGE,
) => {
  const firstGroup = (page - 1) * pageSize;
  return groups.slice(firstGroup, firstGroup + pageSize);
};

const getUserName = (user: any) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || user?.email || 'Unknown member';
};

const getGroupType = (group: TeamsAndGroupsGroup, groupTypes: any[]) => {
  if (group.groupType && typeof group.groupType === 'object') {
    return group.groupType;
  }

  return (
    groupTypes.find((type) => type.id === group.groupType) || {
      id: String(group.groupType || ''),
      name: 'Uncategorised',
    }
  );
};

export const isProtectedGroup = (group: TeamsAndGroupsGroup) =>
  group.isDefault === true ||
  group.isDefault === 'true' ||
  group.name.trim().toLowerCase() === 'general';

const membershipMatchesGroup = (
  membership: any,
  group: TeamsAndGroupsGroup,
) => {
  const value = membership?.group;
  const membershipGroupId =
    typeof value === 'object' ? value?.id : value || membership?.groupId;
  const membershipGroupName =
    typeof value === 'object' ? value?.name : membership?.name;

  return (
    membershipGroupId === group.id ||
    (membershipGroupName && membershipGroupName === group.name)
  );
};

export const resolveGroupMembers = (
  group: TeamsAndGroupsGroup,
  users: any[],
) => {
  if (Array.isArray(group.members) && group.members.length > 0) {
    return group.members
      .map((member: any) => {
        const id = typeof member === 'string' ? member : member?.id;
        return users.find((user) => user.id === id) || member;
      })
      .filter(Boolean);
  }

  return users.filter((user) =>
    user.groups?.some((membership: any) =>
      membershipMatchesGroup(membership, group),
    ),
  );
};

const formatTypeName = (name: string) => {
  const normalizedName = name.toLowerCase();

  if (normalizedName === 'department' || normalizedName === 'departments') {
    return 'Department';
  }

  if (normalizedName === 'team' || normalizedName === 'teams') {
    return 'Teams';
  }

  return name;
};

export const AdminGroupTypePage = observer(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    groupTypeStore: {groupType},
    usersStore: {users},
    authStore: {featureLimitStatus},
  } = useStoreContext();
  const [selectedType, setSelectedType] = useState('all');
  const [page, setPage] = useState(1);
  const [editingGroup, setEditingGroup] = useState<TeamsAndGroupsGroup | null>(
    null,
  );
  const [confirmDeleteOnOpen, setConfirmDeleteOnOpen] = useState(false);
  const [menuGroupId, setMenuGroupId] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {data, isLoading} = useQuery(
    ['teams-and-groups-landing'],
    async () => {
      const request = new groupRequestImpl();
      const controller = new groupController(request);
      return controller.fetchGroups();
    },
    {staleTime: 30_000},
  );

  const groups = useMemo(() => {
    return normalizeGroups(data).sort((first, second) => {
      const firstProtected = isProtectedGroup(first);
      const secondProtected = isProtectedGroup(second);

      if (firstProtected !== secondProtected) return firstProtected ? -1 : 1;
      return first.name.localeCompare(second.name);
    });
  }, [data]);

  const availableTypes = useMemo(() => {
    const types = new Map<string, {id: string; name: string}>();

    groups.forEach((group) => {
      const type = getGroupType(group, groupType);
      if (type?.id && type?.name) {
        types.set(type.id, {id: type.id, name: type.name});
      }
    });

    groupType.forEach((type: any) => {
      const normalizedName = String(type?.name || '').toLowerCase();
      const isDepartment =
        normalizedName === 'department' || normalizedName === 'departments';

      if (isDepartment && type?.id && type?.name) {
        types.set(type.id, {id: type.id, name: type.name});
      }
    });

    return Array.from(types.values());
  }, [groupType, groups]);

  const visibleGroups = useMemo(
    () =>
      selectedType === 'all'
        ? groups
        : groups.filter(
            (group) => getGroupType(group, groupType).id === selectedType,
          ),
    [groupType, groups, selectedType],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(visibleGroups.length / GROUPS_PER_PAGE),
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    const closeMenu = () => setMenuGroupId('');
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const paginatedGroups = useMemo(() => {
    return paginateGroups(visibleGroups, page);
  }, [page, visibleGroups]);

  const paginatedGroupIds = paginatedGroups.map((group) => group.id).join(',');

  const {data: membersByGroup = new Map<string, any[]>()} = useQuery(
    ['teams-and-groups-member-previews', paginatedGroupIds, users.length],
    async () => {
      const request = new groupRequestImpl();
      const controller = new groupController(request);
      const entries = await Promise.all(
        paginatedGroups.map(async (group) => {
          const response = await controller.newfetchGroupMembers(
            group.id,
            false,
            false,
          );
          return [group.id, normalizeGroupMembers(response, users)] as const;
        }),
      );

      return new Map(entries);
    },
    {
      enabled: paginatedGroups.length > 0,
      staleTime: 30_000,
    },
  );

  const reachedGroupLimit =
    featureLimitStatus?.limited &&
    featureLimitStatus?.currentStatus?.groupsCount >=
      featureLimitStatus?.limits?.groups;

  const refreshGroups = async () => {
    await Promise.all([
      queryClient.invalidateQueries(['teams-and-groups-landing']),
      queryClient.invalidateQueries(['teams-and-groups-member-previews']),
    ]);
  };

  return (
    <Page>
      <PageHeader>
        <PageHeading>
          <PageTitle>Teams &amp; Groups</PageTitle>
          <PageSubtitle>
            Organize your workspace into teams and groups.
          </PageSubtitle>
        </PageHeading>
        <Button
          kind="primary"
          style={{height: '46px', padding: '12px 24px'}}
          disabled={reachedGroupLimit}
          onClick={() => setCreateDialogOpen(true)}>
          Create group
        </Button>
      </PageHeader>

      <Filters aria-label="Filter groups by type">
        <FilterButton
          type="button"
          $active={selectedType === 'all'}
          aria-pressed={selectedType === 'all'}
          onClick={() => {
            setSelectedType('all');
            setPage(1);
          }}>
          All {groups.length}
        </FilterButton>
        {availableTypes.map((type) => (
          <FilterButton
            type="button"
            key={type.id}
            $active={selectedType === type.id}
            aria-pressed={selectedType === type.id}
            onClick={() => {
              setSelectedType(type.id);
              setPage(1);
            }}>
            {formatTypeName(type.name)}
          </FilterButton>
        ))}
      </Filters>

      {isLoading ? (
        <Loader aria-label="Loading groups">
          <ItemLoaderLine />
        </Loader>
      ) : visibleGroups.length === 0 ? (
        <EmptyState>
          <strong>No groups found</strong>
          <span>Groups in this category will appear here.</span>
        </EmptyState>
      ) : (
        <GroupGrid>
          {paginatedGroups.map((group) => {
            const type = getGroupType(group, groupType);
            const resolvedMembers = resolveGroupMembers(group, users);
            const hasFetchedMembers = membersByGroup.has(group.id);
            const fetchedMembers = membersByGroup.get(group.id) || [];
            const members = hasFetchedMembers
              ? fetchedMembers
              : resolvedMembers;
            const memberCount = hasFetchedMembers
              ? members.length
              : Math.max(group.membersCount ?? 0, members.length);
            const displayedMembers = members.slice(0, 3);
            const additionalMembers = Math.max(
              memberCount - displayedMembers.length,
              0,
            );
            const groupAdminId =
              typeof group.groupAdmin === 'object'
                ? group.groupAdmin?.id
                : group.groupAdmin;
            const lead =
              (typeof group.groupAdmin === 'object' && group.groupAdmin) ||
              users.find((user) => user.id === groupAdminId);
            const isProtected = isProtectedGroup(group);

            return (
              <GroupCard key={group.id}>
                <CardHeader>
                  <GroupMeta>
                    <GroupTitle>
                      {isProtected && <LockIcon />}
                      <span>{group.name}</span>
                    </GroupTitle>
                  </GroupMeta>
                  <CardActions>
                    <TypePill>
                      {formatTypeName(type.name || 'Uncategorised')}
                    </TypePill>
                    {!isProtected && (
                      <MemberAction
                        onClick={(event) => event.stopPropagation()}>
                        <MemberActionButton
                          type="button"
                          aria-label={`Actions for ${group.name}`}
                          aria-expanded={menuGroupId === group.id}
                          onClick={() =>
                            setMenuGroupId(
                              menuGroupId === group.id ? '' : group.id,
                            )
                          }>
                          <HorizontalDots />
                        </MemberActionButton>
                        {menuGroupId === group.id && (
                          <MemberMenu style={{width: 180}}>
                            <MemberMenuButton
                              type="button"
                              onClick={() => {
                                setMenuGroupId('');
                                setConfirmDeleteOnOpen(false);
                                setEditingGroup(group);
                              }}>
                              Edit group
                            </MemberMenuButton>
                            <MemberMenuButton
                              type="button"
                              $danger
                              onClick={() => {
                                setMenuGroupId('');
                                setConfirmDeleteOnOpen(true);
                                setEditingGroup(group);
                              }}>
                              Delete group
                            </MemberMenuButton>
                          </MemberMenu>
                        )}
                      </MemberAction>
                    )}
                  </CardActions>
                </CardHeader>

                <MemberCount>
                  {memberCount} {memberCount === 1 ? 'member' : 'members'}
                </MemberCount>

                <GroupLead>
                  <LeadLabel>Lead</LeadLabel>
                  {lead ? (
                    <LeadDetails>
                      <Avatar
                        size="sm"
                        tooltip={false}
                        name={getUserName(lead)}
                        userId={lead.id || groupAdminId || group.id}
                        src={lead.avatar?.url || ''}
                      />
                      <LeadName>{getUserName(lead)}</LeadName>
                    </LeadDetails>
                  ) : (
                    <LeadName $muted>No lead assigned</LeadName>
                  )}
                </GroupLead>

                <CardDivider />

                <CardFooter>
                  <MembersSummary aria-label={`${memberCount} group members`}>
                    <MemberAvatars>
                      {displayedMembers.map((member: any, index: number) => (
                        <MemberAvatar key={member.id || `${group.id}-${index}`}>
                          <Avatar
                            size="sm"
                            tooltip={false}
                            name={getUserName(member)}
                            userId={member.id || `${group.id}-${index}`}
                            src={member.avatar?.url || ''}
                          />
                        </MemberAvatar>
                      ))}
                    </MemberAvatars>
                    {additionalMembers > 0 && <span>+{additionalMembers}</span>}
                  </MembersSummary>
                  <ViewGroupButton
                    type="button"
                    onClick={() =>
                      navigate(`/workspace-settings/groups/${group.id}`)
                    }>
                    View group
                    <ArrowRightIcon style={{stroke: '#585adf'}} />
                  </ViewGroupButton>
                </CardFooter>
              </GroupCard>
            );
          })}
        </GroupGrid>
      )}

      {visibleGroups.length > GROUPS_PER_PAGE && (
        <Pagination aria-label="Groups pagination">
          <PaginationButton
            type="button"
            aria-label="Previous groups page"
            disabled={page === 1}
            onClick={() => setPage((currentPage) => currentPage - 1)}>
            <ArrowHeadLeftIcon />
          </PaginationButton>
          {Array.from({length: totalPages}, (_, index) => index + 1).map(
            (pageNumber) => (
              <PaginationButton
                type="button"
                key={pageNumber}
                $active={pageNumber === page}
                aria-label={`Go to groups page ${pageNumber}`}
                aria-current={pageNumber === page ? 'page' : undefined}
                onClick={() => setPage(pageNumber)}>
                {pageNumber}
              </PaginationButton>
            ),
          )}
          <PaginationButton
            type="button"
            aria-label="Next groups page"
            disabled={page === totalPages}
            onClick={() => setPage((currentPage) => currentPage + 1)}>
            <ArrowHeadRightIcon />
          </PaginationButton>
          <PaginationStatus>
            Showing {(page - 1) * GROUPS_PER_PAGE + 1}–
            {Math.min(page * GROUPS_PER_PAGE, visibleGroups.length)} of{' '}
            {visibleGroups.length}
          </PaginationStatus>
        </Pagination>
      )}

      {editingGroup && (
        <GroupEditSidecar
          key={`${editingGroup.id}-${confirmDeleteOnOpen ? 'delete' : 'edit'}`}
          group={editingGroup}
          groupTypes={groupType}
          users={users}
          confirmDeleteOnOpen={confirmDeleteOnOpen}
          onMutated={refreshGroups}
          onClose={() => {
            setEditingGroup(null);
            setConfirmDeleteOnOpen(false);
          }}
        />
      )}

      {createDialogOpen && (
        <CreateGroupDialog
          groupTypes={groupType
            .filter((type: any) => type?.id && type?.name)
            .map((type: any) => ({id: type.id, name: type.name}))}
          members={users}
          onCreated={refreshGroups}
          onClose={() => setCreateDialogOpen(false)}
        />
      )}
    </Page>
  );
});
